import jsPDF from 'jspdf';

const BRAND_GREEN = [74, 124, 89];
const PAGE_W = 210;
const MARGIN = 14;
const CONTENT_W = PAGE_W - MARGIN * 2;
const LINE_H = 6.5;

// ── header ────────────────────────────────────────────────────────────────────
function addHeader(doc, noteType, clientName, date) {
  doc.setFillColor(...BRAND_GREEN);
  doc.rect(0, 0, PAGE_W, 22, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('Avenues Unlimited Counseling Center', PAGE_W / 2, 14, { align: 'center' });

  doc.setTextColor(0, 0, 0);
  doc.setFontSize(15);
  doc.setFont('helvetica', 'bold');
  doc.text(noteType, MARGIN, 33);

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(60, 60, 60);
  doc.text(`Client: ${clientName || '—'}`, MARGIN, 41);
  doc.text(`Date: ${date || '—'}`, MARGIN, 47);

  doc.setDrawColor(200, 200, 200);
  doc.line(MARGIN, 51, PAGE_W - MARGIN, 51);
  return 58;
}

// ── section heading ───────────────────────────────────────────────────────────
function sectionHead(doc, title, y) {
  if (y > 270) { doc.addPage(); y = 20; }
  doc.setFillColor(240, 245, 242);
  doc.rect(MARGIN, y - 4, CONTENT_W, 8, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...BRAND_GREEN);
  doc.text(title.toUpperCase(), MARGIN + 2, y + 1);
  doc.setTextColor(0, 0, 0);
  return y + 9;
}

// ── field block ───────────────────────────────────────────────────────────────
function field(doc, label, value, y) {
  if (y > 272) { doc.addPage(); y = 20; }
  const text = value && String(value).trim() ? String(value).trim() : '—';

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  doc.text(label, MARGIN, y);
  y += 5;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(0, 0, 0);
  const lines = doc.splitTextToSize(text, CONTENT_W);
  lines.forEach(ln => {
    if (y > 278) { doc.addPage(); y = 20; }
    doc.text(ln, MARGIN, y);
    y += LINE_H;
  });
  return y + 3;
}

// ── page footers ──────────────────────────────────────────────────────────────
function addFooters(doc) {
  const total = doc.getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(160, 160, 160);
    doc.text(
      `Avenues Unlimited Counseling Center  •  Confidential  •  Page ${i} of ${total}`,
      PAGE_W / 2, 290, { align: 'center' }
    );
  }
}

// ── note-type formatters ──────────────────────────────────────────────────────
function fmtSOAP(doc, p, y) {
  y = sectionHead(doc, 'Subjective', y);
  y = field(doc, 'Mood', p.subjective?.mood, y);
  y = field(doc, 'Feelings Expressed', p.subjective?.feelingsExpressed, y);
  y = field(doc, 'Session Focus', p.subjective?.sessionFocus, y);

  y = sectionHead(doc, 'Objective', y);
  y = field(doc, 'Interventions Used', p.objective?.interventions, y);
  y = field(doc, 'Additional Techniques', p.objective?.additionalTechniques, y);
  y = field(doc, 'Engagement Level', p.objective?.engagementLevel, y);

  y = sectionHead(doc, 'Assessment', y);
  y = field(doc, 'Client Response', p.assessment?.clientResponse, y);
  y = field(doc, 'Client Progress', p.assessment?.clientProgress, y);

  y = sectionHead(doc, 'Plan', y);
  y = field(doc, 'Plan for Future Sessions', p.plan, y);

  if (p.sessionType) {
    y = sectionHead(doc, 'Session Info', y);
    y = field(doc, 'Session Type', p.sessionType, y);
  }
  return y;
}

const TA_QUESTIONS = [
  ['endVision',       'End Vision — How are you different at end of therapy?'],
  ['currentState',    'Currently'],
  ['desiredState',    'How you would like to be'],
  ['feelings',        'What I am feeling'],
  ['selfBeliefs',     'Beliefs about myself'],
  ['otherBeliefs',    'Beliefs about others'],
  ['actions',         'What I am doing'],
  ['enhanceLife',     'What could you change to enhance your life?'],
  ['howToAccomplish', 'What would you need to do to accomplish that?'],
  ['benefit',         'What about that would be beneficial or pleasing to you?'],
  ['measureSuccess',  'How would we both know you\'ve gotten there?'],
  ['sabotage',        'How could you sabotage your success?'],
  ['avoidSabotage',   'What will you do instead to ensure success?'],
  ['loss',            'Loss'],
  ['gain',            'Gain'],
];

function fmtTA(doc, p, y) {
  y = sectionHead(doc, 'TA Contract Responses', y);
  TA_QUESTIONS.forEach(([key, label]) => {
    if (p[key]) y = field(doc, label, p[key], y);
  });
  return y;
}

const INTAKE_FIELDS = [
  ['presentingProblems',              'Presenting Problems'],
  ['familyMedicalHistory',            'Family Medical History'],
  ['traumaHistory',                   'Trauma History'],
  ['traumaHistoryOther',              'Trauma History (Other)'],
  ['substanceAbuseStatus',            'Substance Abuse Status'],
  ['substanceAbuseFamilyHistory',     'Substance Abuse Family History'],
  ['substanceAbuseFamilyHistoryOther','Substance Abuse Family History (Other)'],
  ['previousTherapyExperiences',      'Previous Therapy Experiences'],
  ['previousTherapyNotes',            'Previous Therapy Notes'],
  ['selfHarmHistory',                 'Self-Harm History'],
  ['relationshipStatus',              'Relationship Status'],
  ['relationshipSatisfaction',        'Relationship Satisfaction'],
  ['protectiveFactors',               'Protective Factors / Strengths'],
  ['copingSkills',                    'Coping Skills'],
  ['mentalStatusExam',                'Mental Status Exam'],
];

function fmtIntake(doc, p, y) {
  INTAKE_FIELDS.forEach(([key, label]) => {
    const val = Array.isArray(p[key]) ? p[key].join(', ') : p[key];
    if (val) y = field(doc, label, val, y);
  });
  return y;
}

function fmtCollateral(doc, p, y) {
  y = sectionHead(doc, 'Contact Details', y);
  y = field(doc, 'Contact Type', p.contactType, y);
  y = field(doc, 'Contact With', p.contactWith, y);
  y = field(doc, 'Duration', p.contactDuration, y);
  y = field(doc, 'Permission to Contact', p.permissionToContact ? 'Yes' : 'No', y);

  y = sectionHead(doc, 'Content', y);
  y = field(doc, 'Purpose', p.contactPurpose, y);
  y = field(doc, 'Summary', p.contactSummary, y);
  y = field(doc, 'Follow-Up', p.followUp, y);
  return y;
}

function fmtSchool(doc, p, y) {
  y = sectionHead(doc, 'Client & Appointment', y);
  y = field(doc, 'Client Name', p.clientName, y);
  y = field(doc, 'Client DOB', p.clientDob, y);
  y = field(doc, 'Appointment Date', p.appointmentDate, y);
  y = field(doc, 'Appointment Day', p.appointmentDayOfWeek, y);
  y = field(doc, 'Appointment Time', p.appointmentTime, y);

  y = sectionHead(doc, 'Therapist', y);
  y = field(doc, 'Name', p.therapistName, y);
  y = field(doc, 'Credentials', p.therapistCredentials, y);
  y = field(doc, 'Title', p.therapistTitle, y);
  y = field(doc, 'Phone', `${p.therapistPhone || ''}${p.therapistPhoneExtension ? ' x' + p.therapistPhoneExtension : ''}`, y);
  y = field(doc, 'Email', p.therapistEmail, y);

  y = sectionHead(doc, 'Note Content', y);
  if (p.customContent && p.customNoteContent) {
    y = field(doc, 'Custom Note', p.customNoteContent, y);
  }
  if (p.additionalNotes) y = field(doc, 'Additional Notes', p.additionalNotes, y);
  return y;
}

function fmtHandwritten(doc, p, y) {
  if (p.title) y = field(doc, 'Title', p.title, y);
  if (p.drawingData) {
    try {
      if (y > 200) { doc.addPage(); y = 20; }
      const imgH = Math.min(120, 270 - y);
      doc.addImage(p.drawingData, 'PNG', MARGIN, y, CONTENT_W, imgH);
      y += imgH + 6;
    } catch (_) {
      y = field(doc, 'Note', 'Handwritten image could not be rendered.', y);
    }
  }
  return y;
}

// ── ROI formatter ─────────────────────────────────────────────────────────────
function fmtROI(doc, s, y) {
  const g = (camel, snake) => s[camel] || s[snake] || '';
  const bool = v => v ? '✓ Yes' : '✗ No';

  y = sectionHead(doc, 'Client Information', y);
  y = field(doc, 'Client Name', g('clientName','client_name'), y);
  y = field(doc, 'Date of Birth', g('clientDob','client_dob'), y);
  y = field(doc, 'Address', g('clientAddress','client_address'), y);
  y = field(doc, 'Phone', g('clientPhone','client_phone'), y);

  y = sectionHead(doc, 'Release Information To', y);
  y = field(doc, 'Name', g('authorizeReleaseTo','authorize_release_to'), y);
  y = field(doc, 'Relationship', g('releaseToRelationship','release_to_relationship'), y);
  y = field(doc, 'Phone / Email', g('releaseToPhone','release_to_phone'), y);

  y = sectionHead(doc, 'Authorized Release', y);
  const checks = [
    [g('shareProgressWithProvider','share_progress_with_provider'),   'Share progress with primary care provider'],
    [g('coordinateCareWithPsychiatrist','coordinate_care_with_psychiatrist'), 'Coordinate care with psychiatrist'],
    [g('shareProgressWithSchool','share_progress_with_school'),       'Share progress with school'],
    [g('releaseSummaryLetter','release_summary_letter'),              'Release summary letter'],
    [g('releaseRecords','release_records'),                           'Release records'],
    [g('exchangeInformation','exchange_information'),                 'Exchange information'],
  ];
  checks.forEach(([val, label]) => {
    if (val !== '' && val !== false && val !== 'false') {
      y = field(doc, label, bool(val), y);
    }
  });

  const purpose = g('purposeOfDisclosure','purpose_of_disclosure');
  if (purpose) y = field(doc, 'Purpose of Disclosure', purpose, y);

  y = sectionHead(doc, 'Authorization', y);
  y = field(doc, 'Expiration', g('authorizationExpiration','authorization_expiration'), y);
  y = field(doc, 'Right to Revoke', g('rightToRevoke','right_to_revoke'), y);
  y = field(doc, 'Date Signed', g('signatureDate','signature_date'), y);

  return y;
}

// ── public API ────────────────────────────────────────────────────────────────
export function generateNotePDF(note, clientName) {
  let p = {};
  try { p = JSON.parse(note.notes); } catch {}

  const noteType = p.type || 'SOAP Note';
  const date = p.date || new Date(note.created_at).toLocaleDateString();
  const doc = new jsPDF();
  let y = addHeader(doc, noteType, clientName, date);

  if      (!p.type || p.type === 'SOAP Note')        y = fmtSOAP(doc, p, y);
  else if (p.type === 'TA Contract')                 y = fmtTA(doc, p, y);
  else if (p.type === 'IntakeAssessment')            y = fmtIntake(doc, p, y);
  else if (p.type === 'CollateralContact')           y = fmtCollateral(doc, p, y);
  else if (p.type === 'SchoolNote')                  y = fmtSchool(doc, p, y);
  else if (p.type === 'HandwrittenNote')             y = fmtHandwritten(doc, p, y);

  addFooters(doc);

  const safeDate  = date.replace(/\//g, '-');
  const safeName  = (clientName || 'Client').replace(/[,\s]+/g, '_');
  const safeType  = noteType.replace(/\s+/g, '_');
  return {
    pdfBase64: doc.output('datauristring').split(',')[1],
    fileName:  `${safeType}_${safeName}_${safeDate}.pdf`,
    noteType,
    date,
  };
}

export function generateROIPDF(submission, clientName) {
  const doc = new jsPDF();
  const date = submission.created_at
    ? new Date(submission.created_at).toLocaleDateString()
    : new Date().toLocaleDateString();

  let y = addHeader(doc, 'Release of Information', clientName, date);
  y = fmtROI(doc, submission, y);
  addFooters(doc);

  const safeName = (clientName || 'Client').replace(/[,\s]+/g, '_');
  return {
    pdfBase64: doc.output('datauristring').split(',')[1],
    fileName:  `ROI_${safeName}_${date.replace(/\//g, '-')}.pdf`,
    noteType:  'Release of Information',
    date,
  };
}
